import { makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import { Brain24Regular, DocumentBulletList24Regular, Settings24Regular } from '@fluentui/react-icons';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useChat } from '../../libs/hooks/useChat';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { ReactComponent as HistoryIcon } from '../../assets/bot-icons/history-btn-icon.svg';
import { ReactComponent as ChatIcon } from '../../assets/bot-icons/dyn-icon-24-comment-chat.svg';

const useStyles = makeStyles({
    sidebar: {
        display: 'flex',
        height: '100%',
    },
    navigation: {
        width: '55px',
        height: '100%',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxShadow: tokens.shadow2,
        zIndex: 1,
    },
    navIcon: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        color: tokens.colorNeutralForeground1,
        width: '100%',
        height: '60px',
        '&:hover': {
            color: tokens.colorNeutralForeground1,
            // Hover background for the *inactive* tab
            '& > div': {
                background: 'var(--app-nav-tab-hover-background)',
            },
        },
    },
    iconWrapper: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        height: '100%',
        paddingTop: '3px',
        transition: 'all 0.2s ease-in-out',
        '& svg': {
            width: '28px',
            height: '28px',
            '& path': {
                // Use currentColor so it inherits from parent's color property
                stroke: 'currentColor',
                fill: 'none',
            },
        },
    },
    navIconActive: {
        '& > div': {
            background: 'var(--app-nav-tab-active-background)',
        },
        color: tokens.colorNeutralForeground1,
        '& span': {
            color: tokens.colorNeutralForeground1,
        },
        '&:hover': {
            color: tokens.colorNeutralForeground1,
            '& span': {
                color: tokens.colorNeutralForeground1,
            },
            '& > div': {
                // Keep active background unchanged on hover
                background: 'var(--app-nav-tab-active-background)',
            },
        },
    },
    navLabel: {
        fontSize: '11px',
        textAlign: 'center',
        width: '100%',
        transition: 'color 0.2s ease-in-out',
    },
});

export const ChatSidebar: React.FC = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const chat = useChat();
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    // Show the Minnen tab only when the org has the feature AND the user has memory on — or has
    // residual notes under an off toggle (an opt-out purges only the current company's scope), so those
    // stay reachable. Seeded app-wide by useInitialUserMemory so the tab appears without visiting Settings.
    const userMemoryEnabled = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags.UserMemoryEnabled,
    );
    const memoryOptedIn = useAppSelector((state: RootState) => state.app.userMemory.optedIn);
    const memoryNoteCount = useAppSelector((state: RootState) => state.app.userMemory.notes.length);
    const showMemories = userMemoryEnabled && (memoryOptedIn || memoryNoteCount > 0);
    const isActive = (path: string) => location.pathname === path;

    const createNewChatIfNeeded = async () => {
        const shouldCreateNewChat = sessionStorage.getItem('shouldCreateNewChat') === 'true';
        if (shouldCreateNewChat) {
            await chat.createChat(hostContext);
            sessionStorage.removeItem('shouldCreateNewChat');
        }
    };

    const handleChatNavigation = async () => {
        try {
            await createNewChatIfNeeded();
            await navigate('/');
        } catch (error) {
            console.error('Failed to navigate or create chat:', error);
        }
    };

    const handleHistoryNavigation = async () => {
        try {
            await navigate('/history');
        } catch (error: unknown) {
            console.error('History navigation failed:', error);
        }
    };

    const handleSettingsNavigation = async () => {
        try {
            await navigate('/settings');
        } catch (error: unknown) {
            console.error('Settings navigation failed:', error);
        }
    };

    const handleMemoriesNavigation = async () => {
        try {
            await navigate('/memories');
        } catch (error: unknown) {
            console.error('Memories navigation failed:', error);
        }
    };

    const handleLicencesNavigation = async () => {
        try {
            await navigate('/licences');
        } catch (error: unknown) {
            console.error('Licences navigation failed:', error);
        }
    };

    return (
        <div className={classes.sidebar}>
            <div className={classes.navigation}>
                <div
                    className={mergeClasses(classes.navIcon, isActive('/') ? classes.navIconActive : undefined)}
                    onClick={() => {
                        void handleChatNavigation().catch((error: unknown) => {
                            console.error('Chat navigation failed:', error);
                        });
                    }}
                >
                    <div className={classes.iconWrapper}>
                        <ChatIcon />
                        <span className={classes.navLabel}>{t('sidebar.chat')}</span>
                    </div>
                </div>
                <div
                    className={mergeClasses(classes.navIcon, isActive('/history') ? classes.navIconActive : undefined)}
                    onClick={() => {
                        void handleHistoryNavigation();
                    }}
                >
                    <div className={classes.iconWrapper}>
                        <HistoryIcon />
                        <Tooltip content={t('sidebar.history')} relationship="label">
                            {/* Use a new translation key for the potentially shorter label */}
                            <span className={classes.navLabel}>{t('sidebar.historyShort')}</span>
                        </Tooltip>
                    </div>
                </div>
                <div
                    className={mergeClasses(classes.navIcon, isActive('/settings') ? classes.navIconActive : undefined)}
                    onClick={() => {
                        void handleSettingsNavigation();
                    }}
                    data-testid="sidebarSettingsTab"
                >
                    <div className={classes.iconWrapper}>
                        <Settings24Regular />
                        <Tooltip content={t('sidebar.settings')} relationship="label">
                            <span className={classes.navLabel}>{t('sidebar.settingsShort')}</span>
                        </Tooltip>
                    </div>
                </div>
                {showMemories && (
                    <div
                        className={mergeClasses(
                            classes.navIcon,
                            isActive('/memories') ? classes.navIconActive : undefined,
                        )}
                        onClick={() => {
                            void handleMemoriesNavigation();
                        }}
                        data-testid="sidebarMemoriesTab"
                    >
                        <div className={classes.iconWrapper}>
                            <Brain24Regular />
                            <Tooltip content={t('sidebar.memories')} relationship="label">
                                <span className={classes.navLabel}>{t('sidebar.memoriesShort')}</span>
                            </Tooltip>
                        </div>
                    </div>
                )}
                <div
                    className={mergeClasses(classes.navIcon, isActive('/licences') ? classes.navIconActive : undefined)}
                    onClick={() => {
                        void handleLicencesNavigation();
                    }}
                    data-testid="sidebarLicencesTab"
                >
                    <div className={classes.iconWrapper}>
                        <DocumentBulletList24Regular />
                        <Tooltip content={t('sidebar.licences')} relationship="label">
                            <span className={classes.navLabel}>{t('sidebar.licencesShort')}</span>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </div>
    );
};
